/**
 * Settings > Users
 *
 * Admin-only user management. List view + per-user actions:
 *   - Assign role / client
 *   - Reset password (shows plaintext once + emails the user)
 *
 * Provisioning (creating a brand new user) still lives in
 * Admin > Agents for now, which sets the agent-specific fields. This
 * page focuses on cross-cutting admin actions on existing users.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi, clientsApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

const ROLES = ['admin', 'supervisor', 'agent', 'qa', 'qa_manager', 'viewer'];

export default function UsersPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [scope, setScope] = useState('all'); // all | employees | client:<id>
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [mfaResetResult, setMfaResetResult] = useState(null);
  const [statusError, setStatusError] = useState(null);

  const setStatusMut = useMutation({
    mutationFn: ({ id, status }) => usersApi.setStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (e) => {
      setStatusError(e.detail || e.message || 'Status change failed');
      setTimeout(() => setStatusError(null), 4000);
    },
  });

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => usersApi.list(),
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => clientsApi.list(),
  });

  const clientName = useMemo(() => {
    const m = {};
    for (const c of clients) m[c.id] = c.name;
    return m;
  }, [clients]);

  const filtered = useMemo(() => {
    let out = users;
    if (scope === 'employees') out = out.filter((u) => !u.client_id);
    else if (scope.startsWith('client:')) {
      const cid = scope.slice(7);
      out = out.filter((u) => u.client_id === cid);
    }
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      out = out.filter((u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q) ||
        (u.extension || '').includes(q)
      );
    }
    return out;
  }, [users, scope, filter]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Users</h2>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Manage role, tenant assignment, and password resets.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <HelpButton moduleKey="settings_users" />
          <button
            onClick={() => setImporting(true)}
            className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm"
          >
            ↑ Import CSV
          </button>
          <button
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm"
          >
            + New user
          </button>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm"
          >
            <option value="all">All users</option>
            <option value="employees">Continuum employees</option>
            {clients.map((c) => (
              <option key={c.id} value={`client:${c.id}`}>Client: {c.name}</option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Filter by name / email / ext"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm w-64"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-surface-muted">Loading…</div>
      ) : (
        <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Email</th>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Role</th>
                <th className="px-3 py-2 text-left font-medium">Extension</th>
                <th className="px-3 py-2 text-left font-medium">Tenant</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">No matching users.</td></tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2">{u.name}</td>
                  <td className="px-3 py-2 capitalize">{u.role}</td>
                  <td className="px-3 py-2">{u.extension || '—'}</td>
                  <td className="px-3 py-2">{u.client_id ? clientName[u.client_id] || '(unknown)' : <em className="text-surface-muted">Continuum</em>}</td>
                  <td className="px-3 py-2">
                    <StatusToggle
                      user={u}
                      onFlip={(next) => setStatusMut.mutate({ id: u.id, status: next })}
                      pending={setStatusMut.isPending && setStatusMut.variables?.id === u.id}
                    />
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(u)} className="text-continuum-600 dark:text-continuum-200 text-sm mr-3">Edit</button>
                    <ResetButton user={u} onResult={setResetResult} />
                    <MfaResetButton user={u} onResult={setMfaResetResult} />
                    <button onClick={() => setDeleting(u)} className="text-red-600 text-sm ml-3">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditUserModal
          user={editing}
          clients={clients}
          onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); }}
        />
      )}

      {resetResult && (
        <ResetResultModal result={resetResult} onClose={() => setResetResult(null)} />
      )}

      {mfaResetResult && (
        <MfaResetResultModal result={mfaResetResult} onClose={() => setMfaResetResult(null)} />
      )}

      {creating && (
        <CreateUserModal
          clients={clients}
          onClose={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); }}
        />
      )}

      {importing && (
        <UsersCsvImportModal
          onClose={() => { setImporting(false); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); }}
        />
      )}

      {statusError && (
        <div className="fixed bottom-4 right-4 px-3 py-2 rounded-thin bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200 text-sm shadow-thin">
          {String(statusError)}
        </div>
      )}

      {deleting && (
        <DeleteUserModal
          user={deleting}
          onClose={() => { setDeleting(null); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); }}
        />
      )}
    </div>
  );
}

function CreateUserModal({ clients, onClose }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('agent');
  const [extension, setExtension] = useState('');
  const [clientId, setClientId] = useState('');
  const [winid, setWinid] = useState('');
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
      };
      if (extension) payload.extension = extension;
      if (winid) payload.winid = winid;
      if (clientId) payload.client_id = clientId;
      return usersApi.create(payload);
    },
    onSuccess: (u) => { setCreated(u); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
    onError: (e) => setError(e.detail || e.message),
  });

  if (created) {
    return <CreatedUserReveal result={created} onClose={onClose} />;
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">New user</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <Labeled label={clientId ? 'Email' : 'Email (Continuum employees: must end in @continuumgbl.com)'}>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              placeholder={clientId ? 'jane.doe@client.com' : 'jane.doe@continuumgbl.com'}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin" />
          </Labeled>
          <Labeled label="Full name">
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin" />
          </Labeled>
          <Labeled label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Labeled>
          <Labeled label="Extension (optional, 4–6 digits)">
            <input value={extension} onChange={(e) => setExtension(e.target.value)} pattern="\d{4,6}"
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin font-mono" />
          </Labeled>
          <Labeled label="WinID (optional, internal tracking)">
            <input value={winid} onChange={(e) => setWinid(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin" />
          </Labeled>
          <Labeled label="Tenant">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
              <option value="">Continuum employee</option>
              {clients.map((c) => <option key={c.id} value={c.id}>Client · {c.name}</option>)}
            </select>
          </Labeled>
          {error && <div className="text-red-600 text-sm">{String(error)}</div>}
          <div className="text-xs text-surface-muted">
            A 18-char temp password will be generated and emailed to the user via Resend (subject to domain verification).
          </div>
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={!email.trim() || !name.trim() || save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {save.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteUserModal({ user, onClose }) {
  const [hard, setHard] = useState(false);
  const [error, setError] = useState(null);
  const remove = useMutation({
    mutationFn: () => usersApi.remove(user.id, { hard }),
    onSuccess: onClose,
    onError: (e) => setError(e.detail || e.message),
  });
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border">
          <h3 className="font-semibold">Delete user</h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p>Deleting <strong>{user.email}</strong>.</p>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={hard} onChange={(e) => setHard(e.target.checked)} />
            <span>Hard delete (purge the row + audit history)</span>
          </label>
          <p className="text-xs text-surface-muted">
            Without hard delete: status flips to <code>disabled</code>, extension is freed for re-use, login is blocked. Reversible by editing status.
          </p>
          {hard && <p className="text-xs text-red-600">Hard delete is permanent. The user’s audit trail is unaffected (audit_events keep the user_id reference even after the row is gone).</p>}
          {error && <div className="text-red-600 text-sm">{String(error)}</div>}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            className="px-3 py-1.5 rounded-thin bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm"
          >
            {remove.isPending ? 'Deleting…' : (hard ? 'Hard delete' : 'Disable')}
          </button>
        </div>
      </div>
    </div>
  );
}


function ResetButton({ user, onResult }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (!window.confirm(`Reset password for ${user.email}?\nA temp password will be shown once and emailed to the user.`)) return;
    setBusy(true);
    try {
      const r = await usersApi.passwordReset(user.id);
      onResult(r);
    } catch (e) {
      window.alert('Failed: ' + (e.detail || e.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={click} disabled={busy} className="text-amber-600 text-sm disabled:opacity-40">
      {busy ? '…' : 'Reset password'}
    </button>
  );
}

function MfaResetButton({ user, onResult }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const enrolled = user.mfa_enrolled;
  const click = async () => {
    if (!window.confirm(
      `Reset MFA for ${user.email}?\n\n` +
      `Their current authenticator (TOTP) enrollment and recovery codes will be cleared. ` +
      `On their next login they'll be prompted to enroll a fresh authenticator entry.`,
    )) return;
    setBusy(true);
    try {
      const r = await usersApi.mfaReset(user.id);
      onResult(r);
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (e) {
      window.alert('Failed: ' + (e.detail || e.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={click}
      disabled={busy}
      title={enrolled ? 'MFA is enrolled — clear it so the user can re-enroll.' : 'User has no MFA enrolled yet.'}
      className="text-amber-600 text-sm disabled:opacity-40 ml-3"
    >
      {busy ? '…' : 'Reset MFA'}
    </button>
  );
}

function MfaResetResultModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border">
          <h3 className="font-semibold">MFA reset ✅</h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p>MFA has been reset for <strong>{result.email}</strong>.</p>
          <div className="text-xs text-surface-muted dark:text-surface-dark-muted">
            {result.was_enrolled
              ? 'Their previous authenticator enrollment and recovery codes were cleared.'
              : 'This user had no MFA enrolled; nothing to clear (state confirmed reset).'}
          </div>
          <div className="text-xs text-continuum-700 dark:text-continuum-200">
            On their next login they'll be prompted to enroll a fresh authenticator
            entry (new QR code + recovery codes). Their password is unchanged.
          </div>
          <div className="text-xs">
            {result.email_sent ? (
              <span className="text-emerald-700 dark:text-emerald-300">✉️ Emailed to {result.email}.</span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400">⚠️ Email delivery failed — notify the user manually to re-enroll on next login.</span>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}

function CreatedUserReveal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(result.temp_password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border">
          <h3 className="font-semibold">User created ✅</h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p><strong>{result.email}</strong> — status: {result.status}.</p>
          <div>
            <div className="text-xs text-surface-muted dark:text-surface-dark-muted mb-1">Temporary password</div>
            <div className="flex items-center gap-2">
              <code className="px-3 py-2 bg-surface-subtle dark:bg-surface-dark-subtle rounded-thin font-mono text-sm flex-1 break-all">
                {result.temp_password}
              </code>
              <button onClick={copy} className="px-3 py-2 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="text-xs text-surface-muted dark:text-surface-dark-muted">
            Expires {new Date(result.expires_at).toLocaleString()}. The user will be forced to reset on first login.
          </div>
          <div className="text-xs">
            {result.email_sent ? (
              <span className="text-emerald-700 dark:text-emerald-300">✉️ Emailed to {result.email}.</span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400">⚠️ Email delivery failed — share manually.{result.email_error ? ` (${result.email_error})` : ''}</span>
            )}
          </div>
          <div className="text-xs text-amber-600">
            This is the only time this password will be shown. Copy it now if you need to share it manually.
          </div>
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}

function ResetResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(result.temp_password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border">
          <h3 className="font-semibold">Password reset</h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p>Temp password for <strong>{result.email}</strong>:</p>
          <div className="flex items-center gap-2">
            <code className="px-3 py-2 bg-surface-subtle dark:bg-surface-dark-subtle rounded-thin font-mono text-sm flex-1 break-all">
              {result.temp_password}
            </code>
            <button onClick={copy} className="px-3 py-2 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div className="text-xs text-surface-muted dark:text-surface-dark-muted">
            Expires {new Date(result.expires_at).toLocaleString()}. {result.email_sent ? 'Emailed to user.' : 'Email delivery failed — share manually.'}
          </div>
          <div className="text-xs text-amber-600">
            This is the only time this password will be shown. Copy it now if you need to share it manually.
          </div>
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ user, clients, onClose }) {
  const qc = useQueryClient();
  const [role, setRole] = useState(user.role);
  const [clientId, setClientId] = useState(user.client_id || '');
  const [name, setName] = useState(user.name || '');
  const [extension, setExtension] = useState(user.extension || '');
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => usersApi.update(user.id, {
      role, name,
      extension: extension || null,
      client_id: clientId || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'users'] }); onClose(); },
    onError: (e) => setError(e.detail || e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">Edit user</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="text-xs text-surface-muted">Email (read-only)</div>
          <div className="font-mono">{user.email}</div>

          <Labeled label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin" />
          </Labeled>
          <Labeled label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Labeled>
          <Labeled label="Extension (4–6 digits, optional)">
            <input value={extension} onChange={(e) => setExtension(e.target.value)} pattern="\d{4,6}"
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin font-mono" />
          </Labeled>
          <Labeled label="Tenant">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin">
              <option value="">Continuum employee</option>
              {clients.map((c) => <option key={c.id} value={c.id}>Client · {c.name}</option>)}
            </select>
          </Labeled>

          {error && <div className="text-red-600 text-sm">{String(error)}</div>}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">{label}</label>
      {children}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Status toggle pill
// ────────────────────────────────────────────────────────────────────

function StatusToggle({ user, onFlip, pending }) {
  const tones = {
    active:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 hover:bg-emerald-200',
    invited:  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    disabled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200 hover:bg-red-200',
  };
  const cls = tones[user.status] || 'bg-surface-subtle text-surface-text';

  if (user.status === 'invited') {
    return (
      <span className={'px-2 py-0.5 rounded-thin text-xs font-medium ' + cls} title="User has not yet completed first-login. Will become Active automatically after password reset + MFA enrollment.">
        invited
      </span>
    );
  }
  const next = user.status === 'active' ? 'disabled' : 'active';
  const verb = user.status === 'active' ? 'Disable' : 'Re-enable';
  const onClick = () => {
    if (!confirm(`${verb} ${user.email}?\n${user.status === 'active' ? 'They will be denied login + SIP REGISTER.' : 'They will be allowed to log in again.'}`)) return;
    onFlip(next);
  };
  return (
    <button
      onClick={onClick}
      disabled={pending}
      className={'px-2 py-0.5 rounded-thin text-xs font-medium transition-colors disabled:opacity-50 ' + cls}
      title={`Click to ${verb.toLowerCase()}`}
    >
      {pending ? '…' : user.status}
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────
// CSV bulk import wizard
// ────────────────────────────────────────────────────────────────────

function UsersCsvImportModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [diff, setDiff] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);

  const runDry = async () => {
    setError(null); setDiff(null);
    if (!file) { setError('Pick a file first.'); return; }
    try {
      const r = await usersApi.importDryRun(file);
      setDiff(r);
    } catch (e) {
      setError(e.detail || e.message);
    }
  };
  const runCommit = async () => {
    if (!file) return;
    setCommitting(true); setError(null);
    try {
      const r = await usersApi.importCommit(file);
      setDiff(r);
    } catch (e) {
      setError(e.detail || e.message);
    } finally {
      setCommitting(false);
    }
  };

  const summary = diff?.summary || {};
  const errorRows = (diff?.rows || []).filter((r) => r.action === 'error');
  const createRows = (diff?.rows || []).filter((r) => r.action === 'create');
  const updateRows = (diff?.rows || []).filter((r) => r.action === 'update');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-3xl bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">Bulk import users (CSV)</h3>
          <button onClick={onClose} className="text-surface-muted">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <details className="text-xs text-surface-muted">
            <summary className="cursor-pointer">CSV format (click to expand)</summary>
            <div className="mt-2 space-y-1">
              <p>Columns: <code>email,name,role,extension,winid,client</code></p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>email</strong> — required. Continuum employees: must end in <code>@continuumgbl.com</code>. Client users: any domain.</li>
                <li><strong>name</strong> — required.</li>
                <li><strong>role</strong> — <code>admin</code>, <code>supervisor</code>, <code>agent</code> (default), <code>qa</code>, <code>qa_manager</code>, <code>viewer</code>.</li>
                <li><strong>extension</strong> — optional, 4–6 digits. When set, SIP creds are generated and pushed to Kamailio automatically.</li>
                <li><strong>winid</strong> — optional tracking ID.</li>
                <li><strong>client</strong> — optional. Client name (e.g. <code>Acme Corp</code>) OR the client UUID. Blank = Continuum employee.</li>
              </ul>
              <p>Existing rows (same email) are <em>updated</em>; new rows are <em>created</em>. Errors are listed in the preview and skipped on commit.</p>
            </div>
          </details>

          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setDiff(null); }}
            className="text-sm"
          />

          {error && <div className="text-red-600 text-sm">{String(error)}</div>}

          {diff && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="px-2 py-1 rounded-thin bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200">
                  Create: <strong>{summary.create || 0}</strong>
                </span>
                <span className="px-2 py-1 rounded-thin bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200">
                  Update: <strong>{summary.update || 0}</strong>
                </span>
                <span className="px-2 py-1 rounded-thin bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200">
                  Error: <strong>{summary.error || 0}</strong>
                </span>
                {summary.created != null && (
                  <span className="px-2 py-1 rounded-thin bg-emerald-200 dark:bg-emerald-800/40 text-emerald-900 dark:text-emerald-100">
                    ✓ Created: <strong>{summary.created}</strong>
                  </span>
                )}
                {summary.updated != null && (
                  <span className="px-2 py-1 rounded-thin bg-blue-200 dark:bg-blue-800/40 text-blue-900 dark:text-blue-100">
                    ✓ Updated: <strong>{summary.updated}</strong>
                  </span>
                )}
              </div>
              {errorRows.length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-thin overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-200">
                      <tr><th className="px-2 py-1 text-left">Line</th><th className="px-2 py-1 text-left">Email</th><th className="px-2 py-1 text-left">Reason</th></tr>
                    </thead>
                    <tbody>
                      {errorRows.map((r, i) => (
                        <tr key={i} className="border-t border-red-100 dark:border-red-800/50">
                          <td className="px-2 py-1 font-mono">{r.line}</td>
                          <td className="px-2 py-1">{r.email || '—'}</td>
                          <td className="px-2 py-1 text-red-700 dark:text-red-300">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(createRows.length + updateRows.length) > 0 && (
                <details className="text-xs text-surface-muted">
                  <summary className="cursor-pointer">Show {createRows.length + updateRows.length} change(s)</summary>
                  <table className="w-full mt-2">
                    <thead><tr><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Action</th><th className="px-2 py-1 text-left">Email</th><th className="px-2 py-1 text-left">Ext</th><th className="px-2 py-1 text-left">Role</th></tr></thead>
                    <tbody>
                      {[...createRows, ...updateRows].slice(0, 200).map((r, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1 font-mono">{r.line}</td>
                          <td className="px-2 py-1">{r.action}</td>
                          <td className="px-2 py-1">{r.email}</td>
                          <td className="px-2 py-1 font-mono">{r.extension || '—'}</td>
                          <td className="px-2 py-1">{r.payload?.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Close</button>
          <button onClick={runDry} disabled={!file} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm disabled:opacity-50">Preview</button>
          <button
            onClick={runCommit}
            disabled={!file || committing || (diff && (summary.create || 0) + (summary.update || 0) === 0)}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {committing ? 'Committing…' : 'Commit'}
          </button>
        </div>
      </div>
    </div>
  );
}
